import { useState, useMemo, useCallback, type CSSProperties } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPublicTimetable, getNowPlaying, listBookmarks, addBookmark, removeBookmark } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { useDynamicSeo } from "@/lib/dynamicSeo";
import { FollowButton } from "@/components/FollowButton";
import { Segmented } from "@/components/ds";
import { ArrowLeft, Calendar, Clock, Heart, MapPin, Search, Share2 } from "lucide-react";
import { toast } from "sonner";

// DA SISTEMARE (redesign): the public timetable endpoint (PublicStage) does not
// return a per-stage colour, so we derive a stable colour by cycling the four DS
// stage tokens by stage index. The design reference colours each stage; once the
// backend exposes stage colour, swap STAGE_PALETTE for the real value.
const STAGE_PALETTE = [
  "var(--tr-stage-main)",
  "var(--tr-stage-stone)",
  "var(--tr-stage-magma)",
  "var(--tr-stage-sunrise)",
] as const;

export function PublicTimetablePage() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [artistSearch, setArtistSearch] = useState("");
  const [showMyProgramme, setShowMyProgramme] = useState(false);
  const isLoggedIn = !!useAuthStore((s) => s.accessToken);

  const { data: festival, isLoading, error } = useQuery({
    queryKey: ["public-timetable", slug],
    queryFn: () => getPublicTimetable(slug!),
    enabled: !!slug,
  });

  const { data: nowPlaying } = useQuery({
    queryKey: ["now-playing", slug],
    queryFn: () => getNowPlaying(slug!),
    enabled: !!slug,
    refetchInterval: 60000, // refresh every minute
  });

  const { data: bookmarks } = useQuery({
    queryKey: ["bookmarks", slug],
    queryFn: () => listBookmarks(slug!),
    enabled: !!slug && isLoggedIn,
  });

  // Per-festival <head> for crawlers (client-side; Googlebot renders it).
  useDynamicSeo(
    festival
      ? {
          title: `${festival.name} — Timetable & Set Times | TRACE`,
          description: `Set times and stage schedule for ${festival.name}${festival.location ? ` in ${festival.location}` : ""}. See who plays when, on which stage.`,
          canonical: `https://trace.formray.io/live/${slug}`,
          og: {
            "og:type": "event",
            "og:title": `${festival.name} — Timetable & set times`,
            "og:description": `The full stage schedule for ${festival.name}.`,
            "og:url": `https://trace.formray.io/live/${slug}`,
          },
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "MusicEvent",
            name: festival.name,
            startDate: festival.start_date,
            endDate: festival.end_date,
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            url: `https://trace.formray.io/live/${slug}`,
            ...(festival.location
              ? { location: { "@type": "Place", name: festival.location } }
              : {}),
          },
        }
      : null,
  );

  const bookmarkedNames = useMemo(() => {
    if (!bookmarks) return new Set<string>();
    return new Set(bookmarks.map((b) => b.artist_name.toLowerCase()));
  }, [bookmarks]);

  const addBookmarkMutation = useMutation({
    mutationFn: (artistName: string) => addBookmark(slug!, artistName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bookmarks", slug] }),
    onError: (err: Error) => {
      if (!err.message.includes("Already")) toast.error(err.message);
    },
  });

  const removeBookmarkMutation = useMutation({
    mutationFn: (artistName: string) => removeBookmark(slug!, artistName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bookmarks", slug] }),
  });

  const toggleBookmark = useCallback((artistName: string) => {
    if (!isLoggedIn) {
      toast("Sign in to save your programme", { description: "Create a free account to bookmark artists" });
      return;
    }
    if (bookmarkedNames.has(artistName.toLowerCase())) {
      removeBookmarkMutation.mutate(artistName);
    } else {
      addBookmarkMutation.mutate(artistName);
    }
  }, [isLoggedIn, bookmarkedNames, addBookmarkMutation, removeBookmarkMutation]);

  // Get unique days
  const days = useMemo(() => {
    if (!festival) return [];
    const daySet = new Set<string>();
    for (const stage of festival.stages) {
      for (const slot of stage.slots) {
        daySet.add(slot.day);
      }
    }
    return Array.from(daySet).sort();
  }, [festival]);

  // Get unique stage names
  const stageNames = useMemo(() => {
    if (!festival) return [];
    return festival.stages.map((s) => s.name);
  }, [festival]);

  // Stable per-stage colour, keyed by stage name (cycles the DS stage palette).
  const stageColor = useMemo(() => {
    const map = new Map<string, string>();
    stageNames.forEach((name, i) => {
      map.set(name, STAGE_PALETTE[i % STAGE_PALETTE.length]!);
    });
    return map;
  }, [stageNames]);

  // Auto-select first day
  const activeDay = selectedDay || days[0] || null;

  // Filter slots for active day, grouped by stage
  type SlotList = typeof festival extends undefined ? never : NonNullable<typeof festival>["stages"][0]["slots"];
  const daySlots = useMemo(() => {
    if (!festival || !activeDay) return new Map<string, SlotList>();
    const map = new Map<string, SlotList>();
    for (const stage of festival.stages) {
      const filtered = stage.slots
        .filter((s) => s.day === activeDay)
        .sort((a, b) => {
          if (!a.start_time && !b.start_time) return 0;
          if (!a.start_time) return 1;
          if (!b.start_time) return -1;
          return a.start_time.localeCompare(b.start_time);
        });
      map.set(stage.name, filtered);
    }
    return map;
  }, [festival, activeDay]);

  // Check if a slot matches artist search
  const matchesSearch = (artistName: string) => {
    if (!artistSearch) return true;
    return artistName.toLowerCase().includes(artistSearch.toLowerCase());
  };

  const hasSearchResults = useMemo(() => {
    if (!artistSearch) return true;
    for (const slots of daySlots.values()) {
      if (slots.some((s) => matchesSearch(s.artist_name))) return true;
    }
    return false;
  }, [daySlots, artistSearch]);

  // Now playing artist names for highlight
  const nowPlayingArtists = useMemo(() => {
    if (!nowPlaying) return new Set<string>();
    return new Set(nowPlaying.map((s) => s.artist_name));
  }, [nowPlaying]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to copy share link");
    }
  };

  const formatDay = (day: string) => {
    const d = new Date(day + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  };

  const formatTime = (t: string | null) => {
    if (!t) return "TBD";
    return t.slice(0, 5);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-ink-3">Loading timetable…</p>
      </div>
    );
  }

  if (error || !festival) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-ink">Timetable not found</h1>
          <p className="mt-2 text-sm text-ink-3">This festival may not be published yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      {/* Back link */}
      <Link
        to="/live"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-3 transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.6} />
        All festivals
      </Link>

      {/* Hero header */}
      <div className="card relative mb-6 overflow-hidden p-6 sm:p-8">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 90% 120% at 90% 10%, color-mix(in oklab, var(--tr-accent) 20%, transparent) 0%, transparent 60%)",
          }}
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent">
              {nowPlaying && nowPlaying.length > 0
                ? "Public timetable · live"
                : "Public timetable"}
            </p>
            <h1
              className="mt-2 text-[28px] font-bold tracking-[-0.03em] sm:text-[34px]"
              data-ds-display
            >
              {festival.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[12px] text-ink-3">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" strokeWidth={1.6} />
                {new Date(festival.start_date + "T00:00:00").toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
                {" — "}
                {new Date(festival.end_date + "T00:00:00").toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
              {festival.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.6} />
                  {festival.location}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* DA SISTEMARE (redesign): FollowButton is a shared component still on
                legacy zinc/cyan tokens — restyle in its own pass. */}
            <FollowButton slug={slug!} />
            <button
              onClick={handleShare}
              className="btn btn--ghost btn--sm"
            >
              <Share2 className="h-3.5 w-3.5" strokeWidth={1.6} />
              {copied ? "Copied!" : "Share"}
            </button>
            {/* My Programme toggle */}
            {isLoggedIn && bookmarkedNames.size > 0 && (
              <button
                onClick={() => setShowMyProgramme(!showMyProgramme)}
                className="btn btn--sm"
                style={
                  showMyProgramme
                    ? ({
                        color: "var(--tr-bad)",
                        borderColor: "color-mix(in oklab, var(--tr-bad) 40%, transparent)",
                        background: "color-mix(in oklab, var(--tr-bad) 12%, transparent)",
                      } as CSSProperties)
                    : ({
                        color: "var(--tr-text-2)",
                        border: "1px solid var(--tr-border)",
                        background: "var(--tr-panel)",
                      } as CSSProperties)
                }
              >
                <Heart className={`h-4 w-4 ${showMyProgramme ? "fill-current" : ""}`} strokeWidth={1.6} />
                My Programme
                <span className="rounded-pill bg-panel-3 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums">
                  {bookmarkedNames.size}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Now playing */}
        {nowPlaying && nowPlaying.length > 0 && (
          <div
            className="relative mt-5 rounded-ds-sm border px-4 py-3"
            style={{
              borderColor: "color-mix(in oklab, var(--tr-ok) 30%, transparent)",
              background: "color-mix(in oklab, var(--tr-ok) 6%, transparent)",
            }}
          >
            <p className="flex items-center gap-2 text-xs font-semibold text-ok">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ok opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-ok" />
              </span>
              Now Playing
            </p>
            <div className="mt-1.5 flex flex-wrap gap-3">
              {nowPlaying.map((slot) => (
                <span key={`${slot.stage_name}-${slot.artist_name}`} className="text-sm text-ink">
                  <span className="font-semibold">{slot.artist_name}</span>
                  <span className="text-ink-3"> @ {slot.stage_name}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls: artist search + stage legend + day switcher */}
      <div className="relative mb-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-4" />
        <input
          type="text"
          value={artistSearch}
          onChange={(e) => setArtistSearch(e.target.value)}
          placeholder="Search artists…"
          className="input pl-10"
        />
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        {/* Stage legend */}
        <div className="flex flex-wrap gap-1.5">
          {stageNames.map((name) => (
            <span key={name} className="chip inline-flex items-center gap-2">
              <i
                className="h-2 w-2 rounded-full"
                style={{ background: stageColor.get(name) }}
              />
              {name}
            </span>
          ))}
        </div>

        {/* Day switcher */}
        {days.length > 0 && (
          <Segmented
            value={activeDay ?? days[0]!}
            onChange={setSelectedDay}
            ariaLabel="Select day"
            options={days.map((day) => ({ value: day, label: formatDay(day) }))}
          />
        )}
      </div>

      {/* Timetable grid */}
      {!hasSearchResults ? (
        <div className="flex flex-col items-center py-12">
          <Search className="h-8 w-8 text-ink-4" strokeWidth={1.6} />
          <p className="mt-3 text-sm text-ink-3">No artists match "{artistSearch}"</p>
          <button
            onClick={() => setArtistSearch("")}
            className="mt-2 text-sm text-ink-3 transition-colors hover:text-ink"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div
          className="grid gap-4 overflow-x-auto"
          style={{ gridTemplateColumns: `repeat(${stageNames.length}, minmax(150px, 1fr))` }}
        >
          {/* Stage headers */}
          {stageNames.map((name) => {
            const color = stageColor.get(name);
            return (
              <div
                key={name}
                className="flex items-center justify-center gap-2 rounded-t-ds-sm border px-4 py-3 text-center text-sm font-bold uppercase tracking-wider text-ink"
                style={{
                  borderColor: `color-mix(in oklab, ${color} 45%, transparent)`,
                  background: `color-mix(in oklab, ${color} 14%, var(--tr-panel))`,
                }}
              >
                <i className="h-2 w-2 rounded-full" style={{ background: color }} />
                {name}
              </div>
            );
          })}

          {/* Slots */}
          {stageNames.map((name) => {
            const color = stageColor.get(name) ?? "var(--tr-stage-main)";
            return (
              <div key={name} className="space-y-1">
                {(daySlots.get(name) || [])
                  .filter((slot) => !showMyProgramme || bookmarkedNames.has(slot.artist_name.toLowerCase()))
                  .map((slot, i) => {
                    const isPlaying = nowPlayingArtists.has(slot.artist_name);
                    const isMatch = matchesSearch(slot.artist_name);
                    const isBookmarked = bookmarkedNames.has(slot.artist_name.toLowerCase());
                    const dimmed = !isMatch && !!artistSearch;
                    return (
                      <div
                        key={i}
                        className="group relative rounded-ds-sm border px-3 py-2.5 transition-colors"
                        style={
                          isPlaying
                            ? ({
                                borderColor: "color-mix(in oklab, var(--tr-ok) 40%, transparent)",
                                background: "color-mix(in oklab, var(--tr-ok) 10%, transparent)",
                              } as CSSProperties)
                            : dimmed
                              ? ({
                                  borderColor: "var(--tr-border-2)",
                                  background: "var(--tr-panel-2)",
                                  opacity: 0.4,
                                } as CSSProperties)
                              : ({
                                  borderColor: `color-mix(in oklab, ${color} 30%, transparent)`,
                                  background: `color-mix(in oklab, ${color} 8%, var(--tr-panel))`,
                                } as CSSProperties)
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-semibold ${
                                isPlaying ? "text-ok" : dimmed ? "text-ink-3" : "text-ink"
                              }`}
                            >
                              {slot.artist_name}
                              {isPlaying && (
                                <span className="ml-2 inline-flex h-1.5 w-1.5 rounded-full bg-ok" />
                              )}
                            </p>
                            <p className="mt-0.5 flex items-center gap-1 font-mono text-xs text-ink-3">
                              <Clock className="h-3 w-3" strokeWidth={1.6} />
                              {formatTime(slot.start_time)}
                              {slot.end_time && ` — ${formatTime(slot.end_time)}`}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleBookmark(slot.artist_name)}
                            className={`shrink-0 rounded-md p-1 transition-colors ${
                              isBookmarked
                                ? "text-bad hover:opacity-80"
                                : "text-ink-4 opacity-75 group-hover:opacity-100 hover:text-ink-2"
                            }`}
                            title={isBookmarked ? "Remove from programme" : "Add to my programme"}
                          >
                            <Heart className={`h-4 w-4 ${isBookmarked ? "fill-current" : ""}`} strokeWidth={1.6} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                {(() => {
                  const visibleSlots = (daySlots.get(name) || [])
                    .filter((slot) => !showMyProgramme || bookmarkedNames.has(slot.artist_name.toLowerCase()));
                  if (visibleSlots.length === 0) return (
                    <div className="rounded-ds-sm border border-dashed border-line-3 px-3 py-4 text-center text-xs text-ink-4">
                      {showMyProgramme ? "No bookmarked artists" : "No sets"}
                    </div>
                  );
                  return null;
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
